import { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import {
  Plus,
  FolderKanban,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  Square,
  CheckSquare,
  X,
  Zap,
  Clock,
  Search,
  Trash2,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  LockOpen,
  LayoutGrid,
  List,
} from 'lucide-react';
import {
  useGetProjects,
  useBulkUpdateProjectStatus,
  useGetMyPendingDeliberations,
  useDeleteProject,
  type Project,
} from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { formatFy } from '@/lib/utils.js';

// ────────────────────────────────────────────────────────────────────────────
// Australian FY helpers
// ────────────────────────────────────────────────────────────────────────────

function currentAuFy(): string {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() >= 6 ? `FY${year}` : `FY${year - 1}`;
}

function buildFyOptions(): string[] {
  const fyStr = currentAuFy();
  const currentYear = parseInt(fyStr.slice(2), 10);
  const options: string[] = [];
  for (let offset = -3; offset <= 1; offset++) {
    options.push(`FY${currentYear + offset}`);
  }
  return options;
}

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

type StatusFilter = 'active' | 'no_rd' | 'archived' | 'completed' | 'cancelled';
type SortKey = 'name' | 'incomeYear' | 'status' | 'tfa' | 'rdHours' | 'totalHours' | 'repos' | 'pending';
interface SortConfig { key: SortKey; dir: 'asc' | 'desc' }

// ────────────────────────────────────────────────────────────────────────────
// "New" badge logic
// ────────────────────────────────────────────────────────────────────────────

const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';

function isNewProject(project: Project): boolean {
  if (project.createdBy !== SYSTEM_USER_ID) return false;
  const lastLogin = localStorage.getItem('edison:last-login-at');
  if (!lastLogin) return false;
  if (!project.createdAt) return false;
  return new Date(project.createdAt) > new Date(lastLogin);
}

// ────────────────────────────────────────────────────────────────────────────
// Number formatter
// ────────────────────────────────────────────────────────────────────────────

const fmt = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 });

// ────────────────────────────────────────────────────────────────────────────
// Skeleton card
// ────────────────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-hairline overflow-hidden bg-card">
      <div className="px-5 py-4 space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 skeleton rounded" />
          <div className="h-5 w-48 skeleton rounded" />
          <div className="h-5 w-16 skeleton rounded-full" />
          <div className="h-5 w-20 skeleton rounded-full" />
        </div>
        <div className="h-3.5 w-2/3 skeleton rounded ml-6" />
      </div>
      <div className="border-t border-hairline px-5 py-3 bg-surface-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-3 w-24 skeleton rounded" />
            <div className="h-3 w-16 skeleton rounded" />
            <div className="h-3 w-12 skeleton rounded" />
          </div>
          <div className="h-3 w-32 skeleton rounded" />
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Skeleton table row
// ────────────────────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <tr className="border-t border-hairline">
      <td className="px-4 py-3"><div className="h-4 w-44 skeleton rounded" /></td>
      <td className="px-4 py-3"><div className="h-4 w-12 skeleton rounded" /></td>
      <td className="px-4 py-3"><div className="h-5 w-16 skeleton rounded-full" /></td>
      <td className="px-4 py-3"><div className="h-5 w-16 skeleton rounded-full" /></td>
      <td className="px-4 py-3 text-right"><div className="h-4 w-10 skeleton rounded ml-auto" /></td>
      <td className="px-4 py-3 text-right"><div className="h-4 w-10 skeleton rounded ml-auto" /></td>
      <td className="px-4 py-3 text-right"><div className="h-4 w-8 skeleton rounded ml-auto" /></td>
      <td className="px-4 py-3 text-right"><div className="h-4 w-14 skeleton rounded ml-auto" /></td>
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Status badge (for table view)
// ────────────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string | undefined | null }) {
  switch (status) {
    case 'active':
      return (
        <span className="inline-flex items-center text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
          Active
        </span>
      );
    case 'no_rd':
      return (
        <span className="inline-flex items-center text-[11px] font-medium bg-surface-2 text-dim border border-hairline rounded-full px-2 py-0.5">
          No R&D
        </span>
      );
    case 'archived':
      return (
        <span className="inline-flex items-center text-[11px] font-medium bg-surface-2 text-dim border border-hairline rounded-full px-2 py-0.5">
          Archived
        </span>
      );
    case 'completed':
      return (
        <span className="inline-flex items-center text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">
          Completed
        </span>
      );
    case 'cancelled':
      return (
        <span className="inline-flex items-center text-[11px] font-medium bg-surface-2 text-dim border border-hairline rounded-full px-2 py-0.5">
          Cancelled
        </span>
      );
    default:
      return <span className="text-dim text-[11px]">—</span>;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// TFA status badge (table variant — compact labels)
// ────────────────────────────────────────────────────────────────────────────

function TfaTableBadge({ locked, stage }: { locked?: boolean | null; stage?: string | null }) {
  if (locked) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        Locked
      </span>
    );
  }
  if (stage) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
        <Clock className="h-3 w-3" aria-hidden="true" />
        Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[11px] font-medium bg-surface-2 text-dim border border-hairline rounded-full px-2 py-0.5">
      Needed
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TFA status badge (card view — unchanged)
// ────────────────────────────────────────────────────────────────────────────

function TfaBadge({ locked, stage }: { locked?: boolean | null; stage?: string | null }) {
  if (locked) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        TFA locked
      </span>
    );
  }
  if (stage) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
        <Clock className="h-3 w-3" aria-hidden="true" />
        TFA in progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-surface-2 text-dim border border-hairline rounded-full px-2 py-0.5">
      TFA needed
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Eligibility status
// ────────────────────────────────────────────────────────────────────────────

function EligibilityStatus({ locked, stage }: { locked?: boolean | null; stage?: string | null }) {
  if (locked) {
    return (
      <div className="flex items-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
        <span className="text-[11.5px] font-medium text-emerald-700">Eligibility established</span>
      </div>
    );
  }
  if (stage) {
    return (
      <div className="flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" aria-hidden="true" />
        <span className="text-[11.5px] font-medium text-amber-700">TFA in progress</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <LockOpen className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" aria-hidden="true" />
      <span className="text-[11.5px] text-dim">Eligibility not established</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────

export function ProjectListPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [selectedFy, setSelectedFy] = useState<string>(currentAuFy());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [, navigate] = useLocation();
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    try {
      const stored = localStorage.getItem('edison:projects-view');
      return stored === 'table' ? 'table' : 'card';
    } catch {
      return 'card';
    }
  });

  const [sort, setSort] = useState<SortConfig>({ key: 'rdHours', dir: 'desc' });

  function handleSort(key: SortKey) {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  }

  const fyOptions = useMemo(() => buildFyOptions(), []);
  const fyAll = selectedFy === 'All';

  const { data, isLoading, error, refetch } = useGetProjects(
    fyAll ? {} : { incomeYear: selectedFy }
  );

  const { data: pendingDelibData } = useGetMyPendingDeliberations();
  const pendingDelibProjectIds = new Set(
    (pendingDelibData?.data?.items ?? []).map((d) => d.projectId)
  );

  const bulkUpdate = useBulkUpdateProjectStatus();
  const deleteProject = useDeleteProject();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    deleteProject.mutate({ id }, {
      onSuccess: () => { setConfirmDeleteId(null); void refetch(); },
      onError: () => { setConfirmDeleteId(null); },
    });
  };

  const allItems = useMemo(() => data?.data?.items ?? [], [data]);

  useEffect(() => {
    if (allItems.length > 0) {
      localStorage.setItem('edison:setup-project', 'true');
    }
  }, [allItems]);

  const counts = useMemo(() => {
    const c = { active: 0, no_rd: 0, archived: 0, completed: 0, cancelled: 0 };
    for (const p of allItems) {
      if (p.status === 'active') c.active++;
      else if (p.status === 'no_rd') c.no_rd++;
      else if (p.status === 'archived') c.archived++;
      else if (p.status === 'completed') c.completed++;
      else if (p.status === 'cancelled') c.cancelled++;
    }
    return c;
  }, [allItems]);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allItems
      .filter((p) => p.status === statusFilter)
      .filter((p) => !q || (p.name ?? '').toLowerCase().includes(q));

    // In card mode: new projects float to top, rest unsorted
    if (viewMode === 'card' && statusFilter === 'active') {
      const newItems = filtered.filter(isNewProject);
      const rest = filtered.filter((p) => !isNewProject(p));
      return [...newItems, ...rest];
    }

    // Table mode: apply sort
    const tfaOrder = (p: (typeof filtered)[0]) => p.tfaLocked ? 0 : p.tfaStage ? 1 : 2;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case 'name': cmp = (a.name ?? '').localeCompare(b.name ?? ''); break;
        case 'incomeYear': cmp = (a.incomeYear ?? '').localeCompare(b.incomeYear ?? ''); break;
        case 'status': cmp = (a.status ?? '').localeCompare(b.status ?? ''); break;
        case 'tfa': cmp = tfaOrder(a) - tfaOrder(b); break;
        case 'rdHours': cmp = parseFloat(String(a.rdHours ?? 0)) - parseFloat(String(b.rdHours ?? 0)); break;
        case 'totalHours': cmp = parseFloat(String(a.totalHours ?? 0)) - parseFloat(String(b.totalHours ?? 0)); break;
        case 'repos': cmp = Number(a.repoCount ?? 0) - Number(b.repoCount ?? 0); break;
        case 'pending': cmp = Number(a.pendingCount ?? 0) - Number(b.pendingCount ?? 0); break;
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [allItems, statusFilter, search, sort, viewMode]);

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === visibleItems.length && visibleItems.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleItems.map((p) => p.id)));
    }
  }

  function clearSelection() {
    setSelectedIds(new Set());
    setBulkError(null);
  }

  function handleBulkAction() {
    const targetStatus = statusFilter === 'no_rd' ? 'active' : 'no_rd';
    setBulkError(null);
    bulkUpdate.mutate(
      { data: { ids: [...selectedIds], status: targetStatus } },
      {
        onSuccess: () => { setSelectedIds(new Set()); void refetch(); },
        onError: () => { setBulkError('Bulk status update failed. Please try again.'); },
      }
    );
  }

  function handleFyChange(fy: string) {
    setSelectedFy(fy);
    setSelectedIds(new Set());
  }

  function handleStatusChange(s: StatusFilter) {
    setStatusFilter(s);
    setSelectedIds(new Set());
  }

  function handleViewMode(mode: 'card' | 'table') {
    setViewMode(mode);
    try {
      localStorage.setItem('edison:projects-view', mode);
    } catch {
      // ignore storage errors
    }
  }

  const filterChips: { value: StatusFilter; label: string }[] = [
    { value: 'active', label: 'Active' },
    { value: 'no_rd', label: 'No R&D' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'archived', label: 'Archived' },
  ];

  const allSelected = visibleItems.length > 0 && selectedIds.size === visibleItems.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Each project establishes eligibility, then captures evidence and time.
          </p>
        </div>
        <Link href="/projects/new">
          <Button>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New project
          </Button>
        </Link>
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {filterChips.map((chip) => {
            const count: number = counts[chip.value as keyof typeof counts] ?? 0;
            const active = statusFilter === chip.value;
            return (
              <button
                key={chip.value}
                onClick={() => handleStatusChange(chip.value)}
                className={`rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors flex items-center gap-1.5 ${
                  active
                    ? 'bg-ink text-background border border-ink'
                    : 'border border-hairline bg-card hover:bg-surface-3 text-foreground'
                }`}
              >
                {chip.label}
                <span
                  className={`inline-flex items-center justify-center rounded-full text-[10px] font-semibold min-w-[1.125rem] h-[1.125rem] px-1 ${
                    active
                      ? 'bg-background/20 text-background'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {isLoading ? '…' : String(count)}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-md border border-hairline bg-card pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:border-primary/40 w-48"
            />
          </div>

          <select
            value={selectedFy}
            onChange={(e) => handleFyChange(e.target.value)}
            className="rounded-md border border-hairline bg-card px-3 py-1.5 text-xs font-medium text-foreground focus:outline-none focus:border-primary/40"
            aria-label="Financial year filter"
          >
            <option value="All">All years</option>
            {fyOptions.map((fy) => (
              <option key={fy} value={fy}>{formatFy(fy)}</option>
            ))}
          </select>

          {/* View toggle */}
          <div
            className="flex items-center gap-0.5 border border-hairline rounded-md p-0.5 bg-card"
            role="group"
            aria-label="View mode"
          >
            <button
              onClick={() => handleViewMode('card')}
              className={`rounded p-1.5 transition-colors ${
                viewMode === 'card'
                  ? 'bg-surface-2 text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              aria-label="Card view"
              aria-pressed={viewMode === 'card'}
            >
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              onClick={() => handleViewMode('table')}
              className={`rounded p-1.5 transition-colors ${
                viewMode === 'table'
                  ? 'bg-surface-2 text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              aria-label="Table view"
              aria-pressed={viewMode === 'table'}
            >
              <List className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Sticky selection bar */}
      {someSelected && (
        <div className="sticky top-0 z-10 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={allSelected ? 'Deselect all' : 'Select all'}
              >
                {allSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4" />}
              </button>
              <span className="text-sm font-medium text-foreground">
                {selectedIds.size} project{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              {(statusFilter === 'active' || statusFilter === 'no_rd') && (
                <Button size="sm" onClick={handleBulkAction} disabled={bulkUpdate.isPending}>
                  {bulkUpdate.isPending
                    ? 'Updating…'
                    : statusFilter === 'no_rd'
                    ? 'Mark as Active'
                    : 'Mark as No R&D'}
                </Button>
              )}
              <button
                onClick={clearSelection}
                className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          {bulkError != null && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {bulkError}
            </p>
          )}
        </div>
      )}

      {/* Error */}
      {error != null && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Failed to load projects.</span>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </Button>
        </div>
      )}

      {/* Loading skeletons — card mode */}
      {isLoading && viewMode === 'card' && (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Loading skeletons — table mode */}
      {isLoading && viewMode === 'table' && (
        <div className="rounded-xl border border-hairline overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">Project</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">FY</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">Status</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">TFA</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">R&amp;D hrs</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">Total hrs</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">Repos</th>
                <th className="text-right text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && error == null && visibleItems.length === 0 && (
        <div className="rounded-xl border border-dashed border-hairline p-16 text-center">
          <FolderKanban className="mx-auto h-10 w-10 text-muted-foreground/30" aria-hidden="true" />
          {statusFilter === 'active' ? (
            <>
              <p className="mt-3 font-medium text-sm">
                No active projects{fyAll ? '' : ` for ${formatFy(selectedFy)}`}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Create a project to start tracking R&D work, or connect a repo to auto-create projects.
              </p>
              <Link href="/projects/new">
                <Button className="mt-4" size="sm">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create first project
                </Button>
              </Link>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-muted-foreground">
                No {statusFilter === 'no_rd' ? 'No R&D' : statusFilter} projects{fyAll ? '' : ` for ${formatFy(selectedFy)}`}.
              </p>
              <button
                onClick={() => handleStatusChange('active')}
                className="mt-2 text-xs text-primary hover:underline"
              >
                Back to Active
              </button>
            </>
          )}
        </div>
      )}

      {/* Card stack */}
      {!isLoading && error == null && visibleItems.length > 0 && viewMode === 'card' && (
        <div className="space-y-3">
          {visibleItems.map((p) => {
            const isNew = statusFilter === 'active' && isNewProject(p);
            const isChecked = selectedIds.has(p.id);
            const rdHrs = parseFloat(String(p.rdHours ?? 0));
            const totalHrs = parseFloat(String(p.totalHours ?? 0));
            const pending = Number(p.pendingCount ?? 0);
            const repos = Number(p.repoCount ?? 0);
            const isNoRd = p.status === 'no_rd';

            return (
              <div
                key={p.id}
                className={`rounded-xl border overflow-hidden bg-card transition-all ${
                  isChecked
                    ? 'border-primary/40 shadow-sm'
                    : 'border-hairline hover:border-muted-foreground/25'
                } ${isNoRd ? 'opacity-70' : ''}`}
              >
                {/* Top section */}
                <div className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }}
                      className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={isChecked ? `Deselect ${p.name}` : `Select ${p.name}`}
                    >
                      {isChecked
                        ? <CheckSquare className="h-4 w-4 text-primary" aria-hidden="true" />
                        : <Square className="h-4 w-4" aria-hidden="true" />
                      }
                    </button>

                    <div className="flex-1 min-w-0">
                      {/* Name + badges row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/projects/${p.id}`)}
                          className="font-semibold text-[15px] text-ink hover:text-primary transition-colors text-left"
                        >
                          {p.name}
                        </button>
                        {isNew && (
                          <Badge className="text-[10px] px-1.5 py-0 shrink-0">
                            <Zap className="h-2.5 w-2.5 mr-0.5" aria-hidden="true" />
                            New
                          </Badge>
                        )}
                        {isNoRd && (
                          <span className="text-[11px] font-medium bg-surface-2 text-dim border border-hairline rounded-full px-2 py-0.5 shrink-0">
                            No R&D
                          </span>
                        )}
                        {p.incomeYear && (
                          <span className="text-[11px] font-mono border border-hairline rounded px-1.5 py-0.5 text-dim shrink-0">
                            {formatFy(p.incomeYear)}
                          </span>
                        )}
                        <TfaBadge locked={p.tfaLocked} stage={p.tfaStage} />
                        {pendingDelibProjectIds.has(p.id) && (
                          <span className="text-[11px] bg-amber-100 text-amber-800 font-medium px-2 py-0.5 rounded-full shrink-0">
                            Your deliberation pending
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {p.description && (
                        <p className="text-[12.5px] text-dim mt-1.5 truncate" title={p.description}>
                          {p.description}
                        </p>
                      )}
                    </div>

                    {/* Open link + delete */}
                    <div className="flex items-center gap-2 shrink-0">
                      {confirmDeleteId === p.id ? (
                        <span className="flex items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                            disabled={deleteProject.isPending}
                            className="text-xs text-destructive font-medium hover:underline disabled:opacity-40"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                            className="text-muted-foreground hover:text-foreground ml-1"
                            aria-label="Cancel delete"
                          >
                            <X className="h-3 w-3" aria-hidden="true" />
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(p.id); }}
                          className="text-muted-foreground/30 hover:text-destructive transition-colors"
                          aria-label={`Delete ${p.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/projects/${p.id}`)}
                        className="flex items-center gap-0.5 text-[12px] text-primary font-medium hover:underline"
                        aria-label={`Open ${p.name}`}
                      >
                        Open
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Bottom metric grid — 4 cols: R&D Hours / Total / Repos / Pending */}
                <div className="border-t border-hairline px-5 py-3 bg-surface-2 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-6">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-label">R&amp;D Hours</span>
                      <span className="font-mono text-[18px] font-semibold tracking-tight text-ink leading-none">
                        {rdHrs > 0 ? fmt.format(rdHrs) : '—'}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-label">Total Hours</span>
                      <span className="font-mono text-[18px] font-semibold tracking-tight text-ink leading-none">
                        {totalHrs > 0 ? fmt.format(totalHrs) : '—'}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-label">Repos</span>
                      <span className="font-mono text-[18px] font-semibold tracking-tight text-ink leading-none">
                        {repos > 0 ? repos : '—'}
                      </span>
                    </div>
                    {pending > 0 && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-label">Pending</span>
                        <span className="font-mono text-[18px] font-semibold tracking-tight text-amber-600 leading-none">{pending}</span>
                      </div>
                    )}
                  </div>

                  <EligibilityStatus locked={p.tfaLocked} stage={p.tfaStage} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table view */}
      {!isLoading && error == null && visibleItems.length > 0 && viewMode === 'table' && (
        <div className="rounded-xl border border-hairline overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-surface-1">
                {(
                  [
                    { key: 'name', label: 'Project', align: 'left' },
                    { key: 'incomeYear', label: 'FY', align: 'left' },
                    { key: 'status', label: 'Status', align: 'left' },
                    { key: 'tfa', label: 'TFA', align: 'left' },
                    { key: 'rdHours', label: 'R&D hrs', align: 'right' },
                    { key: 'totalHours', label: 'Total hrs', align: 'right' },
                    { key: 'repos', label: 'Repos', align: 'right' },
                  ] as { key: SortKey; label: string; align: 'left' | 'right' }[]
                ).map(({ key, label, align }) => (
                  <th
                    key={key}
                    className={`text-[11px] uppercase tracking-wide font-medium px-4 py-3 cursor-pointer select-none hover:text-foreground transition-colors ${
                      align === 'right' ? 'text-right' : 'text-left'
                    } ${sort.key === key ? 'text-foreground' : 'text-muted-foreground'}`}
                    onClick={() => handleSort(key)}
                  >
                    <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
                      {label}
                      {sort.key === key ? (
                        sort.dir === 'asc'
                          ? <ChevronUp className="h-3 w-3" aria-hidden="true" />
                          : <ChevronDown className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <ChevronDown className="h-3 w-3 opacity-20" aria-hidden="true" />
                      )}
                    </span>
                  </th>
                ))}
                <th className="text-right text-[11px] uppercase tracking-wide text-muted-foreground font-medium px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((p) => {
                const rdHrs = parseFloat(String(p.rdHours ?? 0));
                const totalHrs = parseFloat(String(p.totalHours ?? 0));
                const repos = Number(p.repoCount ?? 0);
                return (
                  <tr
                    key={p.id}
                    className="border-t border-hairline hover:bg-surface-2 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/projects/${p.id}`)}
                          className="font-medium text-ink hover:text-primary transition-colors text-left"
                        >
                          {p.name}
                        </button>
                        {pendingDelibProjectIds.has(p.id) && (
                          <span className="text-[10px] bg-amber-100 text-amber-800 font-medium px-1.5 py-0.5 rounded-full shrink-0">
                            Deliberation
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-dim">
                        {p.incomeYear ? formatFy(p.incomeYear) : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3">
                      <TfaTableBadge locked={p.tfaLocked} stage={p.tfaStage} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-ink font-medium text-sm">
                      {rdHrs > 0 ? fmt.format(rdHrs) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-sm text-dim">
                      {totalHrs > 0 ? fmt.format(totalHrs) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-sm text-dim">
                      {repos > 0 ? repos : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`/projects/${p.id}`)}
                        className="inline-flex items-center gap-0.5 text-[12px] text-primary font-medium hover:underline"
                        aria-label={`Open ${p.name}`}
                      >
                        Open
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
